import { useState, useEffect } from 'react';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { Typography, Space, Spin, Button, Divider } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getStripeClientSecret } from '@/api/user';
import toast from 'react-hot-toast';
import { CustomModal } from '../CustomModal';
import { ReloadOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { getLoggedInUser } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';

const { Text, Title, Paragraph } = Typography;

interface ReferralData {
	referralId: string | null;
}

export const PlansModal = ({ isComponentMode = false }: { isComponentMode?: boolean }) => {
	const dispatch = useAppDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);
	const [plansModalOpen, setPlansModalOpen] = useState(true);
	const [showStripeTable, setShowStripeTable] = useState(false);
	const [customerSecret, setCustomerSecret] = useState<string | null>(null);
	const [referralData, setReferralData] = useState<ReferralData>({ referralId: null });

	const hasActiveSubscription =
		loggedInUser?.inboundSubscriptionId &&
		loggedInUser.inboundSubscriptionStatus !== 'canceled' &&
		!loggedInUser.inboundDemo;

	// UseQuery to get Stripe client secret without caching
	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['getStripeClientSecret'],
		queryFn: () => getStripeClientSecret('Client'),
		enabled: Boolean(plansModalOpen && !hasActiveSubscription && loggedInUser?.id),
		retry: 2,
	});

	// Get Tolt referral ID when component mounts
	useEffect(() => {
		const getToltReferral = () => {
			if (
				typeof window !== 'undefined' &&
				'tolt_referral' in window &&
				!referralData.referralId &&
				window.tolt_referral
			) {
				const toltId = window.tolt_referral as string;
				setReferralData({ referralId: toltId });
			} else if (!referralData.referralId && loggedInUser?.referralId) {
				setReferralData({ referralId: loggedInUser.referralId });
			}
		};

		// Initial check
		getToltReferral();

		// Set up interval to check periodically in case script loads after component
		const interval = setInterval(getToltReferral, 1000);

		// Clean up interval
		return () => clearInterval(interval);
	}, []);

	// Handle success and error within useEffect
	useEffect(() => {
		if (data?.success && data?.customerSecret) {
			setCustomerSecret(data.customerSecret);
			setShowStripeTable(true);
		} else {
			setShowStripeTable(false);
			setCustomerSecret(null);
			if (error) {
				toast.error(
					"We're having trouble loading the pricing information. Please try reloading the page.",
				);
			}
		}
	}, [data, error]);

	// Refetch customer secret every 5 minutes to keep it fresh
	useEffect(() => {
		if (showStripeTable && customerSecret && !hasActiveSubscription) {
			const timer = setInterval(
				() => {
					refetch();
				},
				1000 * 60 * 5,
			); // Every 5 minutes

			return () => clearInterval(timer);
		}
	}, [showStripeTable, customerSecret, refetch, hasActiveSubscription]);

	const handleReload = () => {
		window.location.reload();
	};

	const handleContactSupport = () => {
		dispatch(closeModal());
		dispatch(setCurrentPage(ReceptionistDashboardSiderItem.CONTACT_SUPPORT));
	};

	const renderActiveSubscriptionState = () => (
		<Space
			direction="vertical"
			size="large"
			style={{ textAlign: 'center', width: '100%', padding: '40px 20px' }}
		>
			<Space direction="vertical" size="small">
				<Title level={4} style={{ marginBottom: 0 }}>
					You Have an Active Subscription
				</Title>
				<Paragraph type="secondary" style={{ fontSize: '16px', marginBottom: '24px' }}>
					You already have an active subscription. If you're seeing this message and believe you
					don't have an active subscription, please contact our support team for assistance.
				</Paragraph>
			</Space>

			<Button
				icon={<CustomerServiceOutlined />}
				onClick={handleContactSupport}
				size="large"
				style={{ minWidth: '200px' }}
			>
				Contact Support
			</Button>
		</Space>
	);

	const renderErrorState = () => (
		<Space
			direction="vertical"
			size="large"
			style={{ textAlign: 'center', width: '100%', padding: '40px 20px' }}
		>
			<Space direction="vertical" size="small">
				<Title level={4} style={{ marginBottom: 0 }}>
					Having trouble loading our pricing?
				</Title>
				<Paragraph type="secondary" style={{ fontSize: '16px', marginBottom: '24px' }}>
					Don't worry! You can try reloading the page or reach out to our support team for immediate
					assistance.
				</Paragraph>
			</Space>

			<Space direction="vertical" size="middle" style={{ width: '100%' }}>
				<Button
					type="primary"
					icon={<ReloadOutlined />}
					onClick={handleReload}
					size="large"
					style={{ minWidth: '200px' }}
				>
					Reload Page
				</Button>

				<Divider plain>
					<Text type="secondary">Need help?</Text>
				</Divider>

				<Button
					icon={<CustomerServiceOutlined />}
					onClick={handleContactSupport}
					size="large"
					style={{ minWidth: '200px' }}
				>
					Contact Support
				</Button>
				<Paragraph type="secondary" style={{ fontSize: '14px', marginTop: '8px' }}>
					Our support team is ready to help you get started!
				</Paragraph>
			</Space>
		</Space>
	);

	const renderLoadingState = () => (
		<Space
			direction="vertical"
			size="large"
			style={{ textAlign: 'center', width: '100%', padding: '40px 20px' }}
		>
			<Title level={4}>Loading Pricing Plans</Title>
			<Text type="secondary" style={{ fontSize: '16px' }}>
				Just a moment while we fetch our latest pricing...
			</Text>
			<Spin size="large" />
		</Space>
	);

	const renderStripePricingTable = () => {
		if (!customerSecret) {
			return renderErrorState();
		}

		return (
			// @ts-expect-error ignore for now
			<stripe-pricing-table
				pricing-table-id={import.meta.env.VITE_APP_PRICING_TABLE_KEY}
				publishable-key={import.meta.env.VITE_APP_STRIPE_PUBLIC_KEY}
				customer-session-client-secret={customerSecret}
				client-reference-id={referralData?.referralId || 'INBOUND'}
			/>
		);
	};

	const renderContent = () => {
		if (hasActiveSubscription) {
			return renderActiveSubscriptionState();
		}

		if (isLoading) {
			return renderLoadingState();
		}

		return showStripeTable ? renderStripePricingTable() : renderErrorState();
	};

	if (isComponentMode) {
		return renderContent();
	}

	return (
		<CustomModal
			width={'80%'}
			title={null}
			modalId={MODAL_IDS.PLANS_MODAL}
			//@ts-expect-error ignore for now
			onClose={() => {
				dispatch(closeModal());
				setPlansModalOpen(false);
				setShowStripeTable(false);
			}}
			footer={null}
		>
			{renderContent()}
		</CustomModal>
	);
};

export default PlansModal;
